import React from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, BookOpen, Route, Bot, Check, Users2, Zap, ShieldCheck, Layers, ChevronRight, Building2, Sparkles, HeartPulse, Factory, GraduationCap, ShieldAlert, Sprout, Truck, Home as HomeIcon, Rocket, Cpu, TrendingUp, AlertTriangle } from 'lucide-react';
import { pillarPages } from '../data/pillarPages';
import TopBar from '../components/TopBar';
import UserBar from '../components/UserBar';
import Header from '../components/Header';
import Footer from '../components/Footer';
import PortalModal from '../components/PortalModal';
import { useAuth } from '../context/AuthContext';

const iconMap = { HeartPulse, Factory, GraduationCap, ShieldAlert, Sprout, Truck, Home: HomeIcon, Rocket };

const tabs = [
  { slug: 'skyway', label: 'SkyWay (String Transport)', Icon: Route },
  { slug: 'bridge', label: 'SkyWay Bridge', Icon: Building2 },
  { slug: 'ai-robot', label: 'AI Robot', Icon: Bot },
];

const PillarDetail = () => {
  const { slug } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [portalOpen, setPortalOpen] = React.useState(false);
  const openPortal = () => setPortalOpen(true);
  const closePortal = () => setPortalOpen(false);
  const p = pillarPages[slug] || pillarPages.skyway;

  return (
    <div className="min-h-screen bg-[#040d08]">
      {user ? <UserBar /> : <TopBar onOpenPortal={openPortal} />}
      <Header onOpenPortal={openPortal} onNavigate={() => nav('/')} />

      <section className="px-4 md:px-10 py-6 pb-16">
        <div className="mx-auto max-w-[1240px]">
          <div className="rounded-3xl bg-[#fbf4dc] border border-emerald-900/15 shadow-2xl overflow-hidden">
            <div className="p-6 md:p-8 lg:p-10">
              <div className="flex items-center justify-between gap-4 flex-wrap">
                <button onClick={() => nav('/')} className="inline-flex items-center gap-2 rounded-full border border-emerald-900/20 bg-white/60 hover:bg-white text-emerald-900 text-sm px-4 py-2 transition">
                  <ArrowLeft size={14} /> Back to Ecosystem
                </button>
                <span className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.14em] ${p.docBadge ? 'border-emerald-800/30 bg-emerald-100/70 text-emerald-900' : 'border-emerald-800/30 bg-emerald-100/70 text-emerald-900'}`}>
                  <BookOpen size={12} /> {p.docBadge || 'Technical Documentation & History'}
                </span>
              </div>

              {/* Tabs */}
              <div className="mt-6 flex flex-wrap gap-2">
                {tabs.map(({ slug: s, label, Icon }) => (
                  <Link
                    key={s}
                    to={`/pillar/${s}`}
                    className={`inline-flex items-center gap-2 rounded-xl px-4 py-2 text-sm font-medium transition ${slug === s ? 'bg-emerald-950 text-emerald-50 shadow-sm' : 'bg-emerald-100/60 text-emerald-900 hover:bg-emerald-100'}`}
                  >
                    <Icon size={14} /> {label}
                  </Link>
                ))}
              </div>

              {/* Hero */}
              <div className="mt-6 relative rounded-2xl overflow-hidden border border-emerald-900/20">
                <img src={p.hero} alt={p.title} className="h-[300px] w-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent" />
                <div className="absolute inset-0 p-6 md:p-10 flex flex-col justify-end">
                  <span className={`inline-flex items-center gap-1.5 self-start rounded-full px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.14em] ${p.heroBadgeColor === 'cyan' ? 'bg-cyan-300 text-cyan-950' : 'bg-lime-300 text-emerald-950'}`}>
                    {p.badge}
                  </span>
                  <h1 className="mt-3 font-serif text-white text-3xl md:text-5xl leading-tight tracking-tight drop-shadow">
                    {p.title}
                  </h1>
                  <p className="mt-2 max-w-2xl text-emerald-50/90 text-[14px] md:text-[15px] leading-relaxed">
                    {p.tagline}
                  </p>
                </div>
              </div>

              {/* Introduction */}
              <Section icon={BookOpen} title="Introduction">
                <p className="text-emerald-900/80 leading-relaxed text-[15px]">{p.intro}</p>
              </Section>

              {/* Principles */}
              <Section icon={Layers} title={p.principlesTitle || `What is ${p.title}?`}>
                {p.principlesLead && <p className="mb-4 text-emerald-900/80 text-[14px] leading-relaxed">{p.principlesLead}</p>}
                <div className={`grid grid-cols-1 gap-3 ${p.principles.length === 3 ? 'md:grid-cols-3' : 'md:grid-cols-2'}`}>
                  {p.principles.map((pr) => (
                    <div key={pr.n} className="rounded-2xl border border-emerald-900/15 bg-[#fdf9e7] p-5">
                      <div className="h-10 w-10 rounded-xl bg-emerald-950 text-lime-300 font-serif text-lg flex items-center justify-center">{pr.n}</div>
                      <div className="mt-3 text-emerald-950 font-semibold">{pr.t}</div>
                      <p className="mt-1 text-[13.5px] text-emerald-900/70 leading-relaxed">{pr.d}</p>
                    </div>
                  ))}
                </div>
                {p.principlesTailNote && (
                  <p className="mt-4 rounded-xl border border-emerald-500/20 bg-emerald-50/70 px-4 py-3 text-[13.5px] text-emerald-900/80 italic leading-relaxed">
                    {p.principlesTailNote}
                  </p>
                )}
              </Section>

              {/* Main components */}
              <Section icon={Building2} title={p.componentsTitle || 'Main Components'}>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {p.components.map((c, i) => (
                    <div key={i} className="rounded-2xl border border-emerald-900/15 bg-[#fdf9e7] p-5">
                      <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-emerald-700">{String(i + 1).padStart(2, '0')}</div>
                      <div className="mt-1 text-emerald-950 font-semibold">{c.t}</div>
                      <p className="mt-2 text-[13.5px] text-emerald-900/70 leading-relaxed">{c.d}</p>
                      {c.bullets && (
                        <ul className="mt-3 space-y-1.5">
                          {c.bullets.map((b, bi) => (
                            <li key={bi} className="flex items-start gap-2 text-[13px] text-emerald-900/80">
                              <Check size={13} className="mt-0.5 text-emerald-600 shrink-0" /> {b}
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  ))}
                </div>
              </Section>

              {/* How It Works */}
              <Section icon={ChevronRight} title={p.stepsTitle || 'How It Works'}>
                <ol className="relative border-l-2 border-emerald-900/15 ml-4 space-y-4">
                  {p.steps.map((s, i) => (
                    <li key={i} className="pl-6 relative">
                      <span className="absolute -left-[13px] top-0 h-6 w-6 rounded-full bg-emerald-950 text-lime-300 text-[11px] font-semibold flex items-center justify-center">{String(i + 1).padStart(2, '0')}</span>
                      <p className="text-[14px] text-emerald-900/85 leading-relaxed">{s}</p>
                    </li>
                  ))}
                </ol>
              </Section>

              {/* Key Technologies (chips) */}
              {p.keyTech && (
                <Section icon={Cpu} title="Key AI Technologies Used">
                  <div className="rounded-2xl border border-emerald-900/15 bg-[#fdf9e7] p-5">
                    <div className="flex flex-wrap gap-2">
                      {p.keyTech.map((t) => (
                        <span key={t} className="inline-flex items-center gap-1.5 rounded-full border border-emerald-900/20 bg-white/70 text-emerald-900 text-[13px] font-medium px-3.5 py-1.5">
                          <Sparkles size={12} className="text-emerald-700" /> {t}
                        </span>
                      ))}
                    </div>
                  </div>
                </Section>
              )}

              {/* Features List */}
              {p.featuresList && (
                <Section icon={Check} title="Features of AI Robots">
                  <div className="rounded-2xl border border-emerald-900/15 bg-[#fdf9e7] p-5">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2.5">
                      {p.featuresList.map((f) => (
                        <div key={f} className="flex items-start gap-2 text-[13.5px] text-emerald-900/85">
                          <Check size={14} className="mt-0.5 text-emerald-600 shrink-0" /> {f}
                        </div>
                      ))}
                    </div>
                  </div>
                </Section>
              )}

              {/* Categorized Applications */}
              {p.categorizedApps && (
                <Section icon={Layers} title="Applications of AI Robots">
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
                    {p.categorizedApps.map((a) => {
                      const Icon = iconMap[a.icon] || Layers;
                      return (
                        <div key={a.t} className="rounded-2xl border border-emerald-900/15 bg-[#fdf9e7] p-5">
                          <div className="h-10 w-10 rounded-xl bg-emerald-950 flex items-center justify-center text-lime-300">
                            <Icon size={16} />
                          </div>
                          <div className="mt-3 text-emerald-950 font-semibold">{a.t}</div>
                          <p className="mt-1 text-[13px] text-emerald-900/70 leading-relaxed">{a.d}</p>
                        </div>
                      );
                    })}
                  </div>
                </Section>
              )}

              {/* Advantages & Limitations split */}
              {p.advLimits && (
                <Section icon={ShieldCheck} title="Advantages &amp; Limitations">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="rounded-2xl border border-emerald-500/30 bg-emerald-50 p-5">
                      <div className="flex items-center gap-2 text-emerald-900 font-semibold">
                        <TrendingUp size={16} /> Key Advantages
                      </div>
                      <ul className="mt-3 space-y-2.5">
                        {p.advLimits.advantages.map((a) => (
                          <li key={a.t} className="text-[13.5px] text-emerald-950/85">
                            <span className="font-semibold text-emerald-900">{a.t}:</span> {a.d}
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div className="rounded-2xl border border-amber-500/30 bg-amber-50 p-5">
                      <div className="flex items-center gap-2 text-amber-900 font-semibold">
                        <AlertTriangle size={16} /> Current Limitations
                      </div>
                      <ul className="mt-3 space-y-2.5">
                        {p.advLimits.limitations.map((l) => (
                          <li key={l.t} className="text-[13.5px] text-amber-950/85">
                            <span className="font-semibold text-amber-900">{l.t}:</span> {l.d}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </Section>
              )}

              {/* Power OR Technical Features */}
              {p.power && (
                <Section icon={Zap} title="Power Infrastructure">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                    <div className="md:col-span-2 rounded-2xl border border-emerald-900/15 bg-[#fdf9e7] p-5">
                      <div className="text-emerald-950 font-semibold">Clean Energy Integration</div>
                      <p className="mt-2 text-[14px] text-emerald-900/75 leading-relaxed">{p.power.intro}</p>
                    </div>
                    <div className="rounded-2xl border border-emerald-900/15 bg-[#fdf9e7] p-5">
                      <div className="text-emerald-950 font-semibold">Operational Speeds</div>
                      <ul className="mt-3 space-y-2">
                        {p.power.speeds.map((sp) => (
                          <li key={sp.s} className="flex items-center justify-between text-[13.5px]">
                            <span className="text-emerald-900/75">{sp.s}</span>
                            <span className="font-medium text-emerald-950">{sp.v}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </Section>
              )}

              {p.techFeatures && (
                <Section icon={Zap} title="Technical Features">
                  <div className="rounded-2xl border border-emerald-900/15 bg-[#fdf9e7] overflow-hidden">
                    <div className="divide-y divide-emerald-900/10">
                      {p.techFeatures.map((tf, i) => (
                        <div key={i} className="grid grid-cols-1 md:grid-cols-[220px_1fr] gap-2 px-5 py-3">
                          <div className="text-emerald-950 font-semibold text-[14px]">{tf.f}</div>
                          <div className="text-[14px] text-emerald-900/75">{tf.d}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                </Section>
              )}

              {/* Advantages (skipped when advLimits is used) */}
              {!p.hideAdvantagesSection && (
                <Section icon={ShieldCheck} title={p.advantagesTitle || 'System Advantages'}>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {p.advantages.map((a) => (
                      <div key={a.t} className="rounded-2xl border border-emerald-900/15 bg-[#fdf9e7] p-5">
                        <div className="flex items-center gap-2 text-emerald-950 font-semibold">
                          <Check size={16} className="text-emerald-700" /> {a.t}
                        </div>
                        <p className="mt-2 text-[13.5px] text-emerald-900/70 leading-relaxed">{a.d}</p>
                      </div>
                    ))}
                  </div>
                </Section>
              )}

              {/* Benefits Summary */}
              {p.benefits && (
                <Section icon={Check} title="Potential Benefits Summary">
                  <div className="rounded-2xl border border-emerald-900/15 bg-gradient-to-br from-[#e9f7ec] to-[#f4f0d3] p-5">
                    <ul className="grid grid-cols-1 md:grid-cols-2 gap-2.5">
                      {p.benefits.map((b, i) => (
                        <li key={i} className="flex items-start gap-2 text-[14px] text-emerald-950">
                          <Check size={14} className="mt-1 text-emerald-700 shrink-0" /> {b}
                        </li>
                      ))}
                    </ul>
                  </div>
                </Section>
              )}

              {/* Comparison */}
              {p.comparison && (
                <Section icon={Layers} title={`Comparison: ${p.comparison.leftTitle} vs. ${p.comparison.rightTitle}`}>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="rounded-2xl border border-red-500/30 bg-red-50 p-5">
                      <div className="text-red-900 font-semibold">{p.comparison.leftTitle}</div>
                      <ul className="mt-3 space-y-2">
                        {p.comparison.leftItems.map((it, i) => (
                          <li key={i} className="flex items-start gap-2 text-[13.5px] text-red-950/85">
                            <span className="mt-0.5 text-red-500 font-bold">✕</span> {it}
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div className="rounded-2xl border border-emerald-500/40 bg-emerald-50 p-5">
                      <div className="text-emerald-900 font-semibold">{p.comparison.rightTitle}</div>
                      <ul className="mt-3 space-y-2">
                        {p.comparison.rightItems.map((it, i) => (
                          <li key={i} className="flex items-start gap-2 text-[13.5px] text-emerald-950/85">
                            <Check size={14} className="mt-0.5 text-emerald-700 shrink-0" /> {it}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </Section>
              )}

              {/* Applications */}
              {!p.hideAppsSection && (
                <Section icon={Layers} title="Possible Applications">
                  <div className="rounded-2xl border border-emerald-900/15 bg-[#fdf9e7] p-5">
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-2.5">
                      {p.applications && p.applications.map((a) => (
                        <div key={a} className="flex items-center gap-2 text-[14px] text-emerald-900/85">
                          <Check size={14} className="text-emerald-600" /> {a}
                        </div>
                      ))}
                    </div>
                  </div>
                </Section>
              )}

              {/* Challenges + Vision */}
              {!p.hideChallengesSection && (
                <Section icon={ShieldCheck} title="Challenges &amp; Long-Term Vision">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="rounded-2xl border border-amber-500/30 bg-amber-100/50 p-5">
                      <div className="text-emerald-950 font-semibold">Operational Challenges</div>
                      <ul className="mt-3 list-disc pl-5 space-y-1.5 text-[14px] text-emerald-950/85">
                        {p.challenges && p.challenges.map((c, i) => <li key={i}>{c}</li>)}
                      </ul>
                    </div>
                    {p.vision && (
                      <div className="rounded-2xl border border-emerald-500/30 bg-emerald-100/40 p-5">
                        <div className="text-emerald-950 font-semibold">Long-Term Vision</div>
                        <ul className="mt-3 list-disc pl-5 space-y-1.5 text-[14px] text-emerald-950/85">
                          {p.vision.map((v, i) => <li key={i}>{v}</li>)}
                        </ul>
                      </div>
                    )}
                  </div>
                </Section>
              )}

              {/* Future */}
              {p.future && (
                <Section icon={Sparkles} title={p.futureTitle || 'Future Outlook'}>
                  {p.futureLead && <p className="mb-4 text-emerald-900/80 text-[14px] leading-relaxed">{p.futureLead}</p>}
                  <div className="rounded-2xl border border-emerald-900/15 bg-gradient-to-br from-emerald-50 to-lime-50 p-5">
                    <ul className="grid grid-cols-1 md:grid-cols-2 gap-2.5">
                      {p.future.map((f, i) => (
                        <li key={i} className="flex items-start gap-2 text-[14px] text-emerald-950">
                          <span className="text-emerald-600 font-bold shrink-0">✦</span> {f}
                        </li>
                      ))}
                    </ul>
                  </div>
                </Section>
              )}

              {/* Developer */}
              {!p.hideDeveloperSection && p.developer && (
                <Section icon={Users2} title="Developer &amp; Creator">
                  <div className="rounded-2xl border border-emerald-900/15 bg-[#fdf9e7] p-5">
                    <p className="text-[14px] text-emerald-900/80 leading-relaxed">{p.developer}</p>
                  </div>
                </Section>
              )}

              {/* Summary */}
              <Section icon={BookOpen} title={p.summaryTitle || 'Summary'}>
                <div className="rounded-2xl border border-emerald-900/15 bg-gradient-to-br from-emerald-950 to-[#04160d] p-6 md:p-7 text-emerald-50">
                  <p className="text-[15px] leading-relaxed whitespace-pre-line">{p.summary}</p>
                  <button
                    onClick={() => nav('/')}
                    className="mt-5 inline-flex items-center gap-2 rounded-xl bg-lime-300 hover:bg-lime-200 text-emerald-950 font-semibold px-5 py-2.5 transition"
                  >
                    <ArrowLeft size={15} /> Return to Ecosystem Dashboard
                  </button>
                </div>
              </Section>
            </div>
          </div>
        </div>
      </section>

      <Footer
        onOpenPortal={openPortal}
        onNavigate={(v, sub) => {
          const params = new URLSearchParams();
          params.set('view', v);
          if (sub) params.set('sub', sub);
          if (v === 'home') params.set('section', 'pillars');
          window.location.href = `/?${params.toString()}`;
        }}
      />
      <PortalModal open={portalOpen} onClose={closePortal} />
    </div>
  );
};

const Section = ({ icon: Icon, title, children }) => (
  <div className="mt-8">
    <div className="flex items-center gap-2">
      <div className="h-8 w-8 rounded-lg bg-emerald-950 flex items-center justify-center text-lime-300">
        <Icon size={14} />
      </div>
      <h2 className="font-serif text-emerald-950 text-2xl tracking-tight">{title}</h2>
    </div>
    <div className="mt-4">{children}</div>
  </div>
);

export default PillarDetail;
